import { ChevronRight, Pin, Plus, StickyNote } from 'lucide-react-native';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { webTransition } from '@/lib/hover-style';
import { noteDisplayTitle, noteSnippet } from '@/lib/notes';
import type { NoteRow } from '@/db/types';

interface Props {
  notes: NoteRow[];
  onAdd: () => void;
  onOpen: (note: NoteRow) => void;
  /** Optional: open the full Notes archive (defaults to My Home's
   *  Timeline; can be a dedicated /notes page in the future). */
  onSeeAll?: () => void;
  /** Cap shown in the card. Defaults to 4 — enough to feel useful,
   *  not enough to dominate the My Home page. */
  limit?: number;
}

/**
 * Notes card — preview of recent / pinned notes for the active property.
 * Sits beside the Recent activity card on My Home. Tapping "+ New"
 * opens NoteEditorModal in compose mode; tapping a row opens it in
 * edit mode. Notes also appear in the full /timeline view interleaved
 * with task completions.
 */
export function NotesCard({ notes, onAdd, onOpen, onSeeAll, limit = 4 }: Props) {
  const shown = notes.slice(0, limit);
  return (
    <View style={styles.card}>
      <View style={styles.head}>
        <View style={styles.headLeft}>
          <View style={styles.headIcon}>
            <StickyNote size={14} color={Palette.honeyDeep} strokeWidth={2} />
          </View>
          <View>
            <Text style={styles.headTitle}>Notes</Text>
            <Text style={styles.headSub}>
              {notes.length > 0
                ? `${notes.length} note${notes.length === 1 ? '' : 's'} for this home`
                : 'Jot down anything about this home'}
            </Text>
          </View>
        </View>
        <Pressable
          accessibilityLabel="Add note"
          onPress={onAdd}
          style={({ pressed, hovered }) => [
            styles.addBtn,
            webTransition,
            hovered && styles.addBtnHover,
            pressed && { transform: [{ scale: 0.96 }] },
          ]}>
          <Plus size={13} color={Palette.cream} strokeWidth={2.4} />
          <Text style={styles.addBtnText}>New</Text>
        </Pressable>
      </View>

      {notes.length === 0 ? (
        <View style={styles.empty}>
          <Text style={styles.emptyText}>
            Notes are for the stuff that doesn&apos;t fit into a task — a paint
            color, an HOA rule, the breaker that&apos;s really for the porch
            light. Tap <Text style={styles.emptyTextEm}>New</Text> to jot
            your first one.
          </Text>
        </View>
      ) : (
        <View style={styles.list}>
          {shown.map((n) => (
            <Pressable
              key={n.id}
              accessibilityLabel={`Open note ${noteDisplayTitle(n)}`}
              onPress={() => onOpen(n)}
              style={({ pressed, hovered }) => [
                styles.row,
                webTransition,
                hovered && styles.rowHover,
                pressed && { opacity: 0.85 },
              ]}>
              <View style={styles.rowBody}>
                <View style={styles.rowTitleLine}>
                  {n.pinned === 1 ? (
                    <Pin size={11} color={Palette.honeyDeep} strokeWidth={2.2} />
                  ) : null}
                  <Text style={styles.rowTitle} numberOfLines={1}>
                    {noteDisplayTitle(n)}
                  </Text>
                </View>
                <Text style={styles.rowSnippet} numberOfLines={2}>
                  {noteSnippet(n, 110)}
                </Text>
              </View>
              <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.7} />
            </Pressable>
          ))}
          {onSeeAll && notes.length > limit ? (
            <Pressable
              accessibilityLabel={`See all ${notes.length} notes`}
              onPress={onSeeAll}
              style={({ pressed }) => [
                styles.seeAll,
                pressed && { opacity: 0.7 },
              ]}>
              <Text style={styles.seeAllText}>
                Open all {notes.length} in Notes
              </Text>
              <ChevronRight size={12} color={Palette.forest} strokeWidth={2.2} />
            </Pressable>
          ) : null}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
    flex: 1,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: Spacing.three,
  },
  headLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 },
  headIcon: {
    width: 28,
    height: 28,
    borderRadius: 9,
    backgroundColor: Palette.honeySoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
    letterSpacing: 0.1,
  },
  headSub: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkSoft,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: Palette.forest,
  },
  addBtnHover: { backgroundColor: Palette.forestLight },
  addBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.cream,
    letterSpacing: 0.3,
  },
  empty: {
    paddingVertical: Spacing.three,
  },
  emptyText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    lineHeight: 18,
    color: Palette.inkSoft,
  },
  emptyTextEm: {
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  list: { gap: 6 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: Spacing.three,
    paddingVertical: 10,
    borderRadius: Radius.md,
    backgroundColor: Palette.sand,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  rowHover: {
    backgroundColor: Palette.sandDeep,
    borderColor: Palette.line,
  },
  rowBody: { flex: 1, minWidth: 0 },
  rowTitleLine: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  rowTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
    letterSpacing: -0.05,
    flexShrink: 1,
  },
  rowSnippet: {
    marginTop: 2,
    fontFamily: Fonts.sans,
    fontSize: 12.5,
    lineHeight: 16,
    color: Palette.inkSoft,
  },
  seeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    paddingVertical: 6,
    marginTop: 2,
  },
  seeAllText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.forest,
    letterSpacing: 0.2,
  },
});
